"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import HorizontalSmallImageCategoryNews from "@/components/component_horizontal/HorizontalSmallImageCategoryNews";
import { getImageUrl, formatTimeAgo } from "@/lib/api";
type JustInStory = {
  id: string;
  headline: string;
  slug: string;
  publishedAt: number;
  heroImageS3Key?: string;
  sectionName?: string;
  imageMetadata?: any;
};
type Props = {
  limit?: number;
};
const ENDPOINT = "/api/v1/collections/latest-article-stories";

// Module-level cache so multiple JustIn renders on the same page share a
// single network request instead of each fetching the same data.
let cachedPromise: Promise<JustInStory[]> | null = null;
let cachedAt = 0;
const CACHE_TTL_MS = 60_000;
function safeMap(items: any[]): JustInStory[] {
  if (!Array.isArray(items)) return [];
  const out: JustInStory[] = [];
  for (const entry of items) {
    try {
      const story = entry?.story;
      if (!story || typeof story !== "object") continue;
      const slug = story.slug;
      const headline = story.headline;
      if (typeof slug !== "string" || !slug) continue;
      if (typeof headline !== "string" || !headline) continue;
      const ts = story["last-published-at"] || story["published-at"] || story["updated-at"];
      out.push({
        id: String(story.id || story["story-content-id"] || slug),
        headline: headline.trim(),
        slug,
        publishedAt: typeof ts === "number" ? ts : Date.now(),
        heroImageS3Key: typeof story["hero-image-s3-key"] === "string" ? story["hero-image-s3-key"] : undefined,
        sectionName: story.sections?.[0]?.name || undefined,
        imageMetadata: story["hero-image-metadata"] || undefined
      });
    } catch {
      // Skip malformed entries instead of crashing the whole list.
    }
  }
  return out;
}
async function fetchJustIn(limit: number): Promise<JustInStory[]> {
  const now = Date.now();
  if (cachedPromise && now - cachedAt < CACHE_TTL_MS) {
    return cachedPromise.then(list => list.slice(0, limit)).catch(() => []);
  }
  cachedAt = now;
  cachedPromise = (async () => {
    try {
      const res = await fetch(`${ENDPOINT}?limit=${limit}`, {
        cache: "no-store"
      });
      if (!res.ok) return [];
      const data = await res.json();
      return safeMap(data?.items || []);
    } catch {
      return [];
    }
  })();
  return cachedPromise.then(list => list.slice(0, limit)).catch(() => []);
}

// Plain loop of stories rendered with the existing site card design
// (HorizontalSmallImageCategoryNews). No border, no header, no footer —
// matches the standard loop look used in InfiniteScrollArticles /
// ServerInfiniteScroll. `templatetype` is intentionally not passed so the
// live-blog red indicator never shows.
export default function JustIn(t0) {
  const $ = _c(9);
  const {
    limit: t1
  } = t0;
  const limit = t1 === undefined ? 3 : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [stories, setStories] = useState(t2);
  let t3;
  let t4;
  if ($[1] !== limit) {
    t3 = () => {
      let cancelled = false;
      fetchJustIn(limit).then(list => {
        if (!cancelled) {
          setStories(list);
        }
      }).catch(() => {
        if (!cancelled) {
          setStories([]);
        }
      });
      return () => {
        cancelled = true;
      };
    };
    t4 = [limit];
    $[1] = limit;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  if (stories.length === 0) {
    return null;
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <h3 className="text-16-tamil-700 text-1B1B1B px-4 pt-4">LIVE NEWS UPDATES</h3>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== stories) {
    t6 = stories.map(_temp);
    $[5] = stories;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  return t7;
}
function _temp(s) {
  const imageUrl = s.heroImageS3Key ? getImageUrl(s.heroImageS3Key) : "";
  return <HorizontalSmallImageCategoryNews key={s.id} slug={s.slug} title={{
    value: s.headline
  }} imageSrc={{
    value: imageUrl
  }} timeAgo={{
    value: formatTimeAgo(s.publishedAt)
  }} category={{
    hidden: true,
    value: s.sectionName
  }} imageMetadata={s.imageMetadata} />;
}